import type { DemoPill } from './colimind-types';

const SESSION = 'demo-local';

const buildMeasurementRows = (): string[][] => {
  const rows: string[][] = [];
  const lines = ['Line A', 'Line B', 'Line C', 'Line D', 'Line E', 'Line F'];
  const sites = ['Tenant A', 'Tenant B', 'Tenant C'];
  const colors = ['Storm Bay', 'Alpine White', 'Mountain Blue', 'Skyscraper Grey', 'Portimao Blue', 'Sapphire Black'];
  const codes = ['STB', 'ALP', 'MOB', 'SKG', 'POR', 'SAP'];
  for (let i = 0; i < 50; i++) {
    const d = new Date('2026-06-02T14:00:00Z');
    d.setMinutes(d.getMinutes() - i * 12);
    const ci = i % 6;
    rows.push([
      d.toISOString(),
      (0.75 + Math.random() * 0.2).toFixed(3),
      lines[i % 6],
      sites[i % 3],
      colors[ci],
      codes[ci],
    ]);
  }
  return rows;
};

export const KPI_PILLS: DemoPill[] = [
  {
    label: 'Latest KPI — Line A',
    question: 'What is the latest CPI on line A at Tenant A?',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'The latest measurement on **Line A** at **Tenant A** shows a CPI of **87.9 %**, recorded on June 2, 2026.',
      sql_query:
        "SELECT cpi, timestamp_utc, line, site, color_name FROM kpi_snapshots WHERE line = 'Line A' AND site = 'Tenant A' ORDER BY timestamp_utc DESC LIMIT 1",
      sources: [{ table: 'kpi_snapshots', description: 'kpi_snapshots' }],
      query_result: {
        columns: [
          { name: 'cpi', type_name: 'DOUBLE' },
          { name: 'timestamp_utc', type_name: 'STRING' },
          { name: 'line', type_name: 'STRING' },
          { name: 'site', type_name: 'STRING' },
          { name: 'color_name', type_name: 'STRING' },
        ],
        data_array: [['0.879', '2026-06-02T14:32:00Z', 'Line A', 'Tenant A', 'Storm Bay']],
        row_count: 1,
      },
    },
  },
  {
    label: 'Active Alerts',
    question: 'Show me all alerts with severity status',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'There are **6 active alerts** — 2 critical, 3 warnings and 1 info. Immediate action required on **Line D** and **Line C**.',
      sql_query:
        "SELECT alert_id, alert_type, kpi_name, color_name, part, severity, line, site, status FROM alerts WHERE status = 'open' ORDER BY severity DESC",
      sources: [{ table: 'alerts', description: 'alerts' }],
      query_result: {
        columns: [
          { name: 'alert_id', type_name: 'STRING' },
          { name: 'alert_type', type_name: 'STRING' },
          { name: 'kpi_name', type_name: 'STRING' },
          { name: 'color_name', type_name: 'STRING' },
          { name: 'part', type_name: 'STRING' },
          { name: 'severity', type_name: 'STRING' },
          { name: 'line', type_name: 'STRING' },
          { name: 'site', type_name: 'STRING' },
          { name: 'status', type_name: 'STRING' },
        ],
        data_array: [
          ['ALRT-001', 'CPI_ALERT', 'CPI', 'Storm Bay', 'Front Door', 'critical', 'Line D', 'Tenant A', 'open'],
          ['ALRT-002', 'DRIFT_ALERT', 'Delta E', 'Alpine White', 'Bumper', 'critical', 'Line C', 'Tenant B', 'open'],
          ['ALRT-003', 'LP_ALERT', 'CHI', 'Mountain Blue', 'Hood', 'warning', 'Line A', 'Tenant A', 'open'],
          ['ALRT-004', 'OUTLIER_ALERT', 'Streuung', 'Skyscraper Grey', 'Fender', 'warning', 'Line B', 'Tenant C', 'open'],
          ['ALRT-005', 'DELTAE_ALERT', 'Delta E', 'Portimao Blue', 'Door Right', 'warning', 'Line E', 'Tenant A', 'open'],
          ['ALRT-006', 'TOLERANCE_ALERT', 'Parts in Spec', 'Sapphire Black', 'Trunk', 'info', 'Line F', 'Tenant B', 'open'],
        ],
        row_count: 6,
      },
    },
  },
  {
    label: 'Total Measurements',
    question: 'What is the total number of measurements today?',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'A total of **1,284 measurements** have been recorded today across all production lines.',
      sql_query:
        "SELECT COUNT(*) AS total_measurements FROM kpi_snapshots WHERE DATE(timestamp_utc) = CURRENT_DATE",
      sources: [{ table: 'kpi_snapshots', description: 'kpi_snapshots' }],
      query_result: {
        columns: [{ name: 'total_measurements', type_name: 'LONG' }],
        data_array: [['1284']],
        row_count: 1,
      },
    },
  },
  {
    label: 'Last 50 Measurements',
    question: 'Show me the last 50 measurements with all details',
    response: {
      session_id: SESSION,
      conversation_id: SESSION,
      answer_text:
        'Here are the last **50 measurements** across all lines, sorted by time descending.',
      sql_query:
        'SELECT timestamp_utc, cpi, line, site, color_name, color_code FROM kpi_snapshots ORDER BY timestamp_utc DESC LIMIT 50',
      sources: [{ table: 'kpi_snapshots', description: 'kpi_snapshots' }],
      query_result: {
        columns: [
          { name: 'timestamp_utc', type_name: 'STRING' },
          { name: 'cpi', type_name: 'DOUBLE' },
          { name: 'line', type_name: 'STRING' },
          { name: 'site', type_name: 'STRING' },
          { name: 'color_name', type_name: 'STRING' },
          { name: 'color_code', type_name: 'STRING' },
        ],
        data_array: buildMeasurementRows(),
        row_count: 50,
      },
    },
  },
];
